import BugReportOutlinedIcon from "@mui/icons-material/BugReportOutlined";
import CheckCircleOutlineIcon from "@mui/icons-material/CheckCircleOutlined";
import CloseIcon from "@mui/icons-material/Close";
import CropFreeIcon from "@mui/icons-material/CropFree";
import DrawIcon from "@mui/icons-material/Draw";
import UploadFileIcon from "@mui/icons-material/UploadFile";
import {
  Alert,
  Box,
  Button,
  Chip,
  CircularProgress,
  Dialog,
  DialogContent,
  DialogTitle,
  Divider,
  IconButton,
  MenuItem,
  Paper,
  Select,
  Stack,
  TextField,
  ToggleButton,
  ToggleButtonGroup,
  Tooltip,
  Typography,
  useMediaQuery,
  useTheme,
} from "@mui/material";
import { alpha } from "@mui/material/styles";
import React, {
  type ClipboardEvent as ReactClipboardEvent,
  type MouseEvent as ReactMouseEvent,
  type PointerEvent as ReactPointerEvent,
  useCallback,
  useEffect,
  useRef,
  useState,
} from "react";
import { useLocation, useNavigate } from "react-router";

import {
  buildAllowedIssueReportSearch,
  buildDiagnosticsDigest,
  buildElementSelection,
  buildIssueReportElementLabel,
  buildIssueReportPathname,
  buildIssueReportRouteHint,
  buildIssueReportSelectionContext,
  buildIssueReportSuggestionElement,
  buildIssueReportUrl,
  buildPickerConfirmationCardPosition,
  buildPickerSelectionAdvisories,
  dataUrlByteSize,
  type ElementSelection,
  isIssueReportDebugCaptureEnabled,
  normalizeText,
  resolveBroaderSelectableIssueElement,
  resolveSelectableIssueElement,
  selectImageFileFromClipboard,
} from "@/components/assistant/adminIssueReportCapture";
import { requestBroadcastExit } from "@/routing/broadcastLeaveGuard";
import {
  useCreateAdminV2IssueReportDraftMutation,
  useGenerateAdminV2IssueReportBugSuggestionsMutation,
  useSubmitAdminV2IssueReportMutation,
  useValidateAdminV2IssueReportDraftMutation,
} from "@/store/api/baseApi";
import type {
  AdminV2IssueReportBugSuggestion,
  AdminV2IssueReportBugSuggestionReason,
  AdminV2IssueReportCategory,
  AdminV2IssueReportFollowUpAnswer,
  AdminV2IssueReportPriority,
  AdminV2IssueReportTumuStage,
  AdminV2IssueReportValidationResult,
} from "@/types/adminV2";
import { serializeJsonValue } from "@/types/adminV2Json";
import { getErrorMessage } from "@/utils/errorHandling";
import {
  resizeImageFileToPngDataUrl,
  type ResizeImageToPngResult,
} from "@/utils/images/resizeImageFileToPngDataUrl";
import FrontendLoggerFactory from "@/utils/logging/FrontendLoggerFactory";
import {
  buildFrontendDiagnosticsSnapshot,
  type FrontendDiagnosticsSnapshot,
} from "@/utils/logging/frontendTelemetrySink";

type AnnotationTool = "none" | "box" | "arrow" | "blur" | "text";

type AnnotationShape =
  | {
      type: "box";
      x1: number;
      y1: number;
      x2: number;
      y2: number;
    }
  | {
      type: "blur";
      x1: number;
      y1: number;
      x2: number;
      y2: number;
    }
  | {
      type: "arrow";
      x1: number;
      y1: number;
      x2: number;
      y2: number;
    }
  | {
      type: "text";
      x: number;
      y: number;
      text: string;
    };

interface ScreenshotState {
  dataUrl: string;
  byteSize: number;
  fileName: string | null;
  source: "upload" | "paste";
}

interface WidgetPosition {
  left: number;
  top: number;
}

interface WidgetDragState {
  pointerId: number;
  startClientX: number;
  startClientY: number;
  startLeft: number;
  startTop: number;
  width: number;
  height: number;
}

const CATEGORY_OPTIONS: Array<{
  value: AdminV2IssueReportCategory;
  label: string;
  hoverTitle: string;
  hoverBody: string;
  hoverFootnote?: string;
}> = [
  {
    value: "bug",
    label: "Bug",
    hoverTitle: "Broken behavior",
    hoverBody:
      "Choose this when current behavior is wrong or failing compared with expected behavior.",
  },
  {
    value: "requirements_gap",
    label: "Requirements gap",
    hoverTitle: "Missing rule/workflow",
    hoverBody:
      "Choose this when the system works as built but misses an expected policy, rule, or step.",
  },
  {
    value: "feature_request_improvement",
    label: "Feature request/improvement",
    hoverTitle: "Improvement or new capability",
    hoverBody:
      "Choose this for UX/performance improvements or new functionality, not a breakage.",
  },
  {
    value: "feedback",
    label: "Feedback*",
    hoverTitle: "Low-priority feedback or ideas",
    hoverBody:
      "Choose this for low-priority feedback, ideas, suggestions, or observations when you are not yet sure there is a clear action to take.",
    hoverFootnote: "*Does not require validation. Submit directly.",
  },
];

/* eslint-disable no-inline-colors/no-inline-colors -- This widget owns a local issue-category palette rather than shared theme tokens. */
const CATEGORY_BUTTON_TONES: Record<
  AdminV2IssueReportCategory,
  {
    selectedBackground: string;
    selectedHoverBackground: string;
    unselectedBackground: string;
    unselectedHoverBackground: string;
    borderColor: string;
  }
> = {
  bug: {
    selectedBackground: "#b75c66",
    selectedHoverBackground: "#a74b56",
    unselectedBackground: "#f7dde1",
    unselectedHoverBackground: "#f3d0d6",
    borderColor: "#d7a4ab",
  },
  requirements_gap: {
    selectedBackground: "#c98a49",
    selectedHoverBackground: "#b77937",
    unselectedBackground: "#f8e6d1",
    unselectedHoverBackground: "#f4dcc0",
    borderColor: "#dfbe97",
  },
  feature_request_improvement: {
    selectedBackground: "#5f7ea5",
    selectedHoverBackground: "#4f6f96",
    unselectedBackground: "#dfe8f4",
    unselectedHoverBackground: "#d2dfee",
    borderColor: "#afc2da",
  },
  feedback: {
    selectedBackground: "#668d6f",
    selectedHoverBackground: "#567d5f",
    unselectedBackground: "#ddeadf",
    unselectedHoverBackground: "#d1e3d3",
    borderColor: "#acc9b0",
  },
  support_ticket: {
    selectedBackground: "#4f7f86",
    selectedHoverBackground: "#426f76",
    unselectedBackground: "#d9eaed",
    unselectedHoverBackground: "#cde1e5",
    borderColor: "#a9c9cf",
  },
};
/* eslint-enable no-inline-colors/no-inline-colors */

const PRIORITY_OPTIONS: Array<{
  value: AdminV2IssueReportPriority;
  label: string;
}> = [
  { value: "critical", label: "Critical" },
  { value: "very_high", label: "Very High" },
  { value: "high", label: "High" },
  { value: "medium_high", label: "Medium-High" },
  { value: "medium", label: "Medium" },
  { value: "low", label: "Low" },
];

const TUMU_OPTIONS: Array<{
  value: AdminV2IssueReportTumuStage;
  label: string;
}> = [
  { value: "trust_the_numbers", label: "Trust the numbers" },
  { value: "understand_the_numbers", label: "Understand the numbers" },
  { value: "make_decisions_from_the_numbers", label: "Make decisions" },
  { value: "use_hc_regularly", label: "Use HC regularly" },
];

const DEFAULT_PANEL_STATE = {
  category: "bug" as AdminV2IssueReportCategory,
  priority: "medium" as AdminV2IssueReportPriority,
  tumu: null as AdminV2IssueReportTumuStage | null,
  title: "",
  description: "",
  routeHint: "",
};

const ISSUE_QUEUE_ROUTE = "/issue-reports";
const ISSUE_WIDGET_TRIGGER_LABEL = "Log issue";
const ISSUE_WIDGET_TITLE = "Log issue";
const ISSUE_WIDGET_SUBTITLE =
  "Shared issue capture for admin-portal reports. Preview and app reports now start from the preview-site widget and land in the same queue.";

// Must stay aligned with backend validation:
// services/api/src/service/adminV2/issueReportsService.ts (MAX_SCREENSHOT_BYTES)
const MAX_SCREENSHOT_BYTES = 10_000_000;
const PICKER_HINT_WIDTH = 260;
const PICKER_HINT_HEIGHT = 80;
const BUG_SUGGESTION_SLOW_NOTICE_MS = 2_500;
const WIDGET_DRAG_VIEWPORT_MARGIN_PX = 12;
const ISSUE_WIDGET_SHELL_PADDING_PX = 10;
const ISSUE_WIDGET_CONTENT_RAIL_PX = 24;
const ISSUE_WIDGET_BODY_MIN_HEIGHT_PX = 640;
const ISSUE_WIDGET_WORKFLOW_PANEL_MIN_HEIGHT_PX = 420;
/* eslint-disable no-inline-colors/no-inline-colors -- This widget owns a fixed local accent palette for its header, picker, and canvas annotations. */
const ISSUE_WIDGET_HEADER_BACKGROUND = "#35556f";
const ISSUE_WIDGET_ANNOTATION_COLOR = "#ef4444";
const ISSUE_WIDGET_BLUR_FILL = "#0f172a";
const ISSUE_WIDGET_SELECTION_BORDER_COLORS = {
  broader: "#22c55e",
  pending: "#f59e0b",
  active: "#ef4444",
} as const;
/* eslint-enable no-inline-colors/no-inline-colors */
const WIDGET_DRAG_EXCLUDED_SELECTOR =
  'button, a, input, textarea, select, [role="button"], [role="link"]';

const logger = FrontendLoggerFactory.getLogger().withComponent(
  "AdminIssueReportWidget",
);

type BugSuggestionStatus = "idle" | "loading" | "selecting" | "ready";
type BugSuggestionUiReason =
  AdminV2IssueReportBugSuggestionReason | "accepted" | "manual";

interface BugSuggestionState {
  status: BugSuggestionStatus;
  reason: BugSuggestionUiReason | null;
  suggestions: AdminV2IssueReportBugSuggestion[];
  topConfidence: number | null;
}

const getSuggestionReviewLabel = (
  reason: BugSuggestionUiReason | null,
): string => {
  if (reason === "confident") {
    return "Ready to edit";
  }

  return "Review before validating";
};

const drawArrow = (
  context: CanvasRenderingContext2D,
  x1: number,
  y1: number,
  x2: number,
  y2: number,
): void => {
  context.strokeStyle = ISSUE_WIDGET_ANNOTATION_COLOR;
  context.lineWidth = 4;
  context.lineCap = "round";

  context.beginPath();
  context.moveTo(x1, y1);
  context.lineTo(x2, y2);
  context.stroke();

  const angle = Math.atan2(y2 - y1, x2 - x1);
  const headLength = 14;

  context.beginPath();
  context.moveTo(x2, y2);
  context.lineTo(
    x2 - headLength * Math.cos(angle - Math.PI / 7),
    y2 - headLength * Math.sin(angle - Math.PI / 7),
  );
  context.lineTo(
    x2 - headLength * Math.cos(angle + Math.PI / 7),
    y2 - headLength * Math.sin(angle + Math.PI / 7),
  );
  context.closePath();
  context.fillStyle = ISSUE_WIDGET_ANNOTATION_COLOR;
  context.fill();
};

const drawAnnotationShape = (
  context: CanvasRenderingContext2D,
  shape: AnnotationShape,
): void => {
  if (shape.type === "box") {
    context.strokeStyle = ISSUE_WIDGET_ANNOTATION_COLOR;
    context.lineWidth = 3;
    context.strokeRect(
      Math.min(shape.x1, shape.x2),
      Math.min(shape.y1, shape.y2),
      Math.abs(shape.x2 - shape.x1),
      Math.abs(shape.y2 - shape.y1),
    );
    return;
  }

  if (shape.type === "blur") {
    context.fillStyle = alpha(ISSUE_WIDGET_BLUR_FILL, 0.72);
    context.fillRect(
      Math.min(shape.x1, shape.x2),
      Math.min(shape.y1, shape.y2),
      Math.abs(shape.x2 - shape.x1),
      Math.abs(shape.y2 - shape.y1),
    );
    return;
  }

  if (shape.type === "arrow") {
    drawArrow(context, shape.x1, shape.y1, shape.x2, shape.y2);
    return;
  }

  context.fillStyle = ISSUE_WIDGET_ANNOTATION_COLOR;
  context.font = "18px Roboto, sans-serif";
  context.fillText(shape.text, shape.x, shape.y);
};

const IssueScreenshotAnnotationCanvas: React.FC<{
  sourceDataUrl: string;
  onChange: (value: { dataUrl: string; byteSize: number }) => void;
}> = ({ sourceDataUrl, onChange }) => {
  const canvasRef = useRef<HTMLCanvasElement | null>(null);
  const imageRef = useRef<HTMLImageElement | null>(null);
  const [tool, setTool] = useState<AnnotationTool>("none");
  const [annotations, setAnnotations] = useState<AnnotationShape[]>([]);
  const [draftShape, setDraftShape] = useState<AnnotationShape | null>(null);
  const [isDrawing, setIsDrawing] = useState(false);

  useEffect(() => {
    const image = new Image();
    image.onload = () => {
      imageRef.current = image;
      const canvas = canvasRef.current;
      if (!canvas) return;

      canvas.width = image.naturalWidth;
      canvas.height = image.naturalHeight;
      setAnnotations([]);
      setDraftShape(null);
    };
    image.src = sourceDataUrl;
  }, [sourceDataUrl]);

  useEffect(() => {
    const canvas = canvasRef.current;
    const image = imageRef.current;
    if (!canvas || !image) return;

    const context = canvas.getContext("2d");
    if (!context) return;

    context.clearRect(0, 0, canvas.width, canvas.height);
    context.drawImage(image, 0, 0, canvas.width, canvas.height);

    for (const annotation of annotations) {
      drawAnnotationShape(context, annotation);
    }

    if (draftShape) {
      drawAnnotationShape(context, draftShape);
    }

    const dataUrl = canvas.toDataURL("image/png");
    onChange({
      dataUrl,
      byteSize: dataUrlByteSize(dataUrl),
    });
  }, [annotations, draftShape, onChange]);

  const toCanvasPoint = (event: ReactMouseEvent<HTMLCanvasElement>) => {
    const canvas = canvasRef.current;
    if (!canvas) {
      return { x: 0, y: 0 };
    }

    const rect = canvas.getBoundingClientRect();
    const scaleX = canvas.width / rect.width;
    const scaleY = canvas.height / rect.height;

    return {
      x: (event.clientX - rect.left) * scaleX,
      y: (event.clientY - rect.top) * scaleY,
    };
  };

  const onMouseDown = (event: ReactMouseEvent<HTMLCanvasElement>) => {
    if (tool === "none") return;

    const point = toCanvasPoint(event);

    if (tool === "text") {
      const text = window.prompt("Annotation text");
      if (!text?.trim()) {
        return;
      }

      setAnnotations((previous) => [
        ...previous,
        {
          type: "text",
          x: point.x,
          y: point.y,
          text: text.trim().slice(0, 120),
        },
      ]);
      return;
    }

    setIsDrawing(true);
    setDraftShape({
      type: tool,
      x1: point.x,
      y1: point.y,
      x2: point.x,
      y2: point.y,
    });
  };

  const onMouseMove = (event: ReactMouseEvent<HTMLCanvasElement>) => {
    if (!isDrawing || !draftShape || draftShape.type === "text") return;

    const point = toCanvasPoint(event);
    setDraftShape({
      ...draftShape,
      x2: point.x,
      y2: point.y,
    });
  };

  const onMouseUp = () => {
    if (!isDrawing || !draftShape) return;

    setIsDrawing(false);
    setAnnotations((previous) => [...previous, draftShape]);
    setDraftShape(null);
  };

  return (
    <Stack spacing={1}>
      <Stack direction="row" spacing={1} useFlexGap sx={{ flexWrap: "wrap" }}>
        <ToggleButtonGroup
          exclusive
          size="small"
          value={tool}
          onChange={(_event, value) => {
            if (typeof value === "string") {
              setTool(value as AnnotationTool);
            }
          }}
        >
          <ToggleButton value="none">None</ToggleButton>
          <ToggleButton value="box">Box</ToggleButton>
          <ToggleButton value="arrow">Arrow</ToggleButton>
          <ToggleButton value="blur">Blur/Redact</ToggleButton>
          <ToggleButton value="text">Text</ToggleButton>
        </ToggleButtonGroup>

        <Button
          size="small"
          variant="text"
          onClick={() => {
            setAnnotations([]);
            setDraftShape(null);
          }}
        >
          Clear annotations
        </Button>
      </Stack>

      <Box
        sx={{
          border: (theme) => `1px solid ${theme.palette.divider}`,
          borderRadius: 1,
          overflow: "hidden",
          backgroundColor: "common.white",
        }}
      >
        <Box
          component="canvas"
          ref={canvasRef}
          onMouseDown={onMouseDown}
          onMouseMove={onMouseMove}
          onMouseUp={onMouseUp}
          onMouseLeave={onMouseUp}
          sx={{
            width: "100%",
            maxHeight: 360,
            objectFit: "contain",
            cursor:
              tool === "none"
                ? "default"
                : tool === "text"
                  ? "text"
                  : "crosshair",
          }}
        />
      </Box>
    </Stack>
  );
};

interface AdminIssueReportWidgetProps {
  currentUserId?: string;
  /**
   * Nav PRD §7: rendered inside the shell's Log issue side panel. The panel
   * owns opening, closing and the header; the capture itself is unchanged.
   */
  hosted?: { isOpen?: boolean; onClose: () => void };
}

export const AdminIssueReportWidget: React.FC<AdminIssueReportWidgetProps> = ({
  hosted,
}) => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("sm"));
  const navigate = useNavigate();
  const location = useLocation();
  const sanitizedPathname = buildIssueReportPathname(location.pathname);

  const [standaloneOpen, setIsOpen] = useState(false);
  const isOpen = hosted ? (hosted.isOpen ?? true) : standaloneOpen;
  const closeWidget = () => {
    setIsOpen(false);
    hosted?.onClose();
  };
  const [draftState, setDraftState] = useState(DEFAULT_PANEL_STATE);
  const [uiError, setUiError] = useState<string | null>(null);
  const [screenshotState, setScreenshotState] =
    useState<ScreenshotState | null>(null);
  const [annotatedDataUrl, setAnnotatedDataUrl] = useState<string | null>(null);
  const [annotatedByteSize, setAnnotatedByteSize] = useState<number | null>(
    null,
  );
  const [elementSelection, setElementSelection] =
    useState<ElementSelection | null>(null);
  const [pendingElementSelection, setPendingElementSelection] =
    useState<ElementSelection | null>(null);
  const [pendingBroaderElementSelection, setPendingBroaderElementSelection] =
    useState<ElementSelection | null>(null);
  const [
    isPreviewingBroaderElementSelection,
    setIsPreviewingBroaderElementSelection,
  ] = useState(false);
  const [pickerActive, setPickerActive] = useState(false);
  const [pickerHoverRect, setPickerHoverRect] = useState<{
    top: number;
    left: number;
    width: number;
    height: number;
  } | null>(null);
  const [pickerGuidanceDismissed, setPickerGuidanceDismissed] = useState(false);
  const [pickerCursorPosition, setPickerCursorPosition] = useState<{
    x: number;
    y: number;
  } | null>(null);
  const [isDeveloperContextOpen, setIsDeveloperContextOpen] = useState(false);
  const [developerDiagnosticsSnapshot, setDeveloperDiagnosticsSnapshot] =
    useState<FrontendDiagnosticsSnapshot | null>(null);
  const [bugSuggestionState, setBugSuggestionState] =
    useState<BugSuggestionState>({
      status: "idle",
      reason: null,
      suggestions: [],
      topConfidence: null,
    });
  const [issueReportId, setIssueReportId] = useState<string | null>(null);
  const [validationResult, setValidationResult] =
    useState<AdminV2IssueReportValidationResult | null>(null);
  const [followUpAnswers, setFollowUpAnswers] = useState<
    Record<string, string>
  >({});
  const [additionalContext, setAdditionalContext] = useState("");
  const [currentAdminUrl, setCurrentAdminUrl] = useState<string>(() => {
    if (typeof window === "undefined") {
      return "";
    }

    return buildIssueReportUrl(
      window.location.pathname,
      window.location.search,
    );
  });
  const [showSuggestionSlowNotice, setShowSuggestionSlowNotice] =
    useState(false);
  const [widgetPosition, setWidgetPosition] = useState<WidgetPosition | null>(
    null,
  );
  const [isDraggingWidget, setIsDraggingWidget] = useState(false);

  const pickerHoverElementRef = useRef<HTMLElement | null>(null);
  const pendingBroaderElementRef = useRef<HTMLElement | null>(null);
  const latestSuggestionRequestRef = useRef(0);
  const latestSuggestionKeyRef = useRef<string | null>(null);
  const latestSuggestionStartedAtRef = useRef<number | null>(null);
  const fileInputRef = useRef<HTMLInputElement | null>(null);
  const widgetPaperRef = useRef<HTMLDivElement | null>(null);
  const widgetHeaderRef = useRef<HTMLDivElement | null>(null);
  const widgetDragStateRef = useRef<WidgetDragState | null>(null);
  const widgetBodyUserSelectRef = useRef<string | null>(null);

  const [generateBugSuggestions] =
    useGenerateAdminV2IssueReportBugSuggestionsMutation();
  const [createDraft, createDraftState] =
    useCreateAdminV2IssueReportDraftMutation();
  const [validateDraft, validateDraftState] =
    useValidateAdminV2IssueReportDraftMutation();
  const [submitIssueReport, submitIssueReportState] =
    useSubmitAdminV2IssueReportMutation();
  const lastUiErrorRef = useRef<string | null>(null);

  const clearValidationProgress = useCallback(() => {
    setIssueReportId(null);
    setValidationResult(null);
    setFollowUpAnswers({});
    setAdditionalContext("");
  }, []);

  const resetPanel = () => {
    latestSuggestionRequestRef.current += 1;
    latestSuggestionKeyRef.current = null;
    latestSuggestionStartedAtRef.current = null;
    setDraftState({
      ...DEFAULT_PANEL_STATE,
      routeHint: buildIssueReportRouteHint(location.pathname, location.search),
    });
    setUiError(null);
    setScreenshotState(null);
    setAnnotatedDataUrl(null);
    setAnnotatedByteSize(null);
    setElementSelection(null);
    setPendingElementSelection(null);
    setPendingBroaderElementSelection(null);
    setIsPreviewingBroaderElementSelection(false);
    pendingBroaderElementRef.current = null;
    setBugSuggestionState({
      status: "idle",
      reason: null,
      suggestions: [],
      topConfidence: null,
    });
    setShowSuggestionSlowNotice(false);
    setIssueReportId(null);
    setValidationResult(null);
    setFollowUpAnswers({});
    setAdditionalContext("");
    setIsDeveloperContextOpen(false);
    setDeveloperDiagnosticsSnapshot(null);
  };

  useEffect(() => {
    if (!isOpen) {
      return;
    }

    setDraftState((previous) => ({
      ...previous,
      routeHint: buildIssueReportRouteHint(location.pathname, location.search),
    }));
  }, [isOpen, location.pathname, location.search]);

  useEffect(() => {
    if (!isOpen) {
      return;
    }

    if (typeof window === "undefined") {
      setCurrentAdminUrl(
        buildIssueReportUrl(location.pathname, location.search),
      );
      return;
    }

    setCurrentAdminUrl(buildIssueReportUrl(location.pathname, location.search));
  }, [isOpen, location.pathname, location.search]);

  useEffect(() => {
    if (!uiError || lastUiErrorRef.current === uiError) {
      if (!uiError) {
        lastUiErrorRef.current = null;
      }
      return;
    }

    lastUiErrorRef.current = uiError;
    logger.warn("Admin issue widget surfaced a user-visible error", {
      operation: "issue_report_widget_ui_error",
      route: sanitizedPathname,
      isOpen,
      issueReportId,
      category: draftState.category,
      uiError,
    });
  }, [draftState.category, isOpen, issueReportId, sanitizedPathname, uiError]);

  useEffect(() => {
    if (isOpen) {
      return;
    }

    widgetDragStateRef.current = null;
    setPickerActive(false);
    setPickerHoverRect(null);
    setIsDraggingWidget(false);
    setWidgetPosition(null);

    if (typeof document !== "undefined") {
      document.body.style.userSelect = widgetBodyUserSelectRef.current ?? "";
    }
    widgetBodyUserSelectRef.current = null;
  }, [isOpen]);

  const clampWidgetPosition = useCallback(
    (
      nextPosition: WidgetPosition,
      size: { width: number; height: number },
    ): WidgetPosition => {
      if (typeof window === "undefined") {
        return nextPosition;
      }

      const maxLeft = Math.max(
        WIDGET_DRAG_VIEWPORT_MARGIN_PX,
        window.innerWidth - size.width - WIDGET_DRAG_VIEWPORT_MARGIN_PX,
      );
      const maxTop = Math.max(
        WIDGET_DRAG_VIEWPORT_MARGIN_PX,
        window.innerHeight - size.height - WIDGET_DRAG_VIEWPORT_MARGIN_PX,
      );

      return {
        left: Math.min(
          Math.max(nextPosition.left, WIDGET_DRAG_VIEWPORT_MARGIN_PX),
          maxLeft,
        ),
        top: Math.min(
          Math.max(nextPosition.top, WIDGET_DRAG_VIEWPORT_MARGIN_PX),
          maxTop,
        ),
      };
    },
    [],
  );

  const endWidgetDrag = useCallback(() => {
    const dragState = widgetDragStateRef.current;
    const headerElement = widgetHeaderRef.current;

    if (dragState && headerElement?.hasPointerCapture(dragState.pointerId)) {
      headerElement.releasePointerCapture(dragState.pointerId);
    }

    widgetDragStateRef.current = null;
    setIsDraggingWidget(false);

    if (typeof document !== "undefined") {
      document.body.style.userSelect = widgetBodyUserSelectRef.current ?? "";
    }
    widgetBodyUserSelectRef.current = null;
  }, []);

  const handleWidgetHeaderPointerDown = useCallback(
    (event: ReactPointerEvent<HTMLDivElement>) => {
      if (isMobile) {
        return;
      }

      const target = event.target;
      if (
        target instanceof Element &&
        target.closest(WIDGET_DRAG_EXCLUDED_SELECTOR)
      ) {
        return;
      }

      const paperElement = widgetPaperRef.current;
      if (!paperElement) {
        return;
      }

      const rect = paperElement.getBoundingClientRect();
      const startPosition = widgetPosition ?? {
        left: rect.left,
        top: rect.top,
      };

      widgetDragStateRef.current = {
        pointerId: event.pointerId,
        startClientX: event.clientX,
        startClientY: event.clientY,
        startLeft: startPosition.left,
        startTop: startPosition.top,
        width: rect.width,
        height: rect.height,
      };

      if (typeof document !== "undefined") {
        widgetBodyUserSelectRef.current = document.body.style.userSelect;
        document.body.style.userSelect = "none";
      }

      event.currentTarget.setPointerCapture(event.pointerId);
      setWidgetPosition(startPosition);
      setIsDraggingWidget(true);
    },
    [isMobile, widgetPosition],
  );

  const handleWidgetHeaderPointerMove = useCallback(
    (event: ReactPointerEvent<HTMLDivElement>) => {
      const dragState = widgetDragStateRef.current;
      if (!dragState) {
        return;
      }

      event.preventDefault();

      setWidgetPosition(
        clampWidgetPosition(
          {
            left:
              dragState.startLeft + (event.clientX - dragState.startClientX),
            top: dragState.startTop + (event.clientY - dragState.startClientY),
          },
          {
            width: dragState.width,
            height: dragState.height,
          },
        ),
      );
    },
    [clampWidgetPosition],
  );

  useEffect(() => {
    if (typeof window === "undefined" || !isDraggingWidget || isMobile) {
      return;
    }

    const onPointerMove = (event: PointerEvent) => {
      const dragState = widgetDragStateRef.current;
      if (!dragState) {
        return;
      }

      setWidgetPosition(
        clampWidgetPosition(
          {
            left:
              dragState.startLeft + (event.clientX - dragState.startClientX),
            top: dragState.startTop + (event.clientY - dragState.startClientY),
          },
          {
            width: dragState.width,
            height: dragState.height,
          },
        ),
      );
    };

    const onPointerEnd = () => {
      if (!widgetDragStateRef.current) {
        return;
      }

      endWidgetDrag();
    };

    window.addEventListener("pointermove", onPointerMove);
    window.addEventListener("pointerup", onPointerEnd);
    window.addEventListener("pointercancel", onPointerEnd);

    return () => {
      window.removeEventListener("pointermove", onPointerMove);
      window.removeEventListener("pointerup", onPointerEnd);
      window.removeEventListener("pointercancel", onPointerEnd);
    };
  }, [clampWidgetPosition, endWidgetDrag, isDraggingWidget, isMobile]);

  useEffect(() => {
    if (typeof window === "undefined" || !widgetPosition || isMobile) {
      return;
    }

    const handleResize = () => {
      const rect = widgetPaperRef.current?.getBoundingClientRect();
      if (!rect) {
        return;
      }

      setWidgetPosition((previous) => {
        if (!previous) {
          return previous;
        }

        return clampWidgetPosition(previous, {
          width: rect.width,
          height: rect.height,
        });
      });
    };

    window.addEventListener("resize", handleResize);
    return () => {
      window.removeEventListener("resize", handleResize);
    };
  }, [clampWidgetPosition, isMobile, widgetPosition]);

  useEffect(() => {
    if (bugSuggestionState.status !== "loading") {
      setShowSuggestionSlowNotice(false);
      return;
    }

    const timeoutId = window.setTimeout(() => {
      setShowSuggestionSlowNotice(true);
    }, BUG_SUGGESTION_SLOW_NOTICE_MS);

    return () => {
      window.clearTimeout(timeoutId);
    };
  }, [bugSuggestionState.status]);

  const updatePendingSelectionFromElement = useCallback(
    (element: HTMLElement, source: "picker" | "broadened") => {
      const nextSelection = buildElementSelection(element);
      const advisories = buildPickerSelectionAdvisories(
        nextSelection.rect,
        window.innerWidth,
        window.innerHeight,
      );
      const broaderElement = resolveBroaderSelectableIssueElement(
        element,
        window.innerWidth,
        window.innerHeight,
      );

      setElementSelection(null);
      setPendingElementSelection(nextSelection);
      setPendingBroaderElementSelection(
        broaderElement ? buildElementSelection(broaderElement) : null,
      );
      setIsPreviewingBroaderElementSelection(false);
      pendingBroaderElementRef.current = broaderElement;
      clearValidationProgress();

      logger.info(
        source === "picker"
          ? "Issue report element picked for confirmation"
          : "Issue report element selection broadened",
        {
          operation:
            source === "picker"
              ? "issue_report_picker_candidate_selected"
              : "issue_report_picker_selection_broadened",
          category: draftState.category,
          route: sanitizedPathname,
          elementTagName: nextSelection.tagName,
          elementRole: nextSelection.role,
          elementDisplay: nextSelection.display,
          htmlSnapshotLength: nextSelection.selectedHtml?.length ?? 0,
          advisoryCodes: advisories.map((advisory) => advisory.code),
          hasBroaderSuggestion: Boolean(broaderElement),
        },
      );
    },
    [clearValidationProgress, draftState.category, sanitizedPathname],
  );

  useEffect(() => {
    if (!isOpen || !pickerActive) {
      setPickerHoverRect(null);
      setPickerCursorPosition(null);
      setPickerGuidanceDismissed(false);
      pickerHoverElementRef.current = null;
      return;
    }

    const onMouseMove = (event: MouseEvent) => {
      setPickerCursorPosition({
        x: event.clientX,
        y: event.clientY,
      });

      const target = event.target;
      if (!(target instanceof Element)) return;

      const selectableTarget = resolveSelectableIssueElement(target);
      if (!selectableTarget) {
        setPickerHoverRect(null);
        pickerHoverElementRef.current = null;
        return;
      }

      const rect = selectableTarget.getBoundingClientRect();
      setPickerHoverRect({
        top: rect.top,
        left: rect.left,
        width: rect.width,
        height: rect.height,
      });
      pickerHoverElementRef.current = selectableTarget;
    };

    const onClick = (event: MouseEvent) => {
      const hovered = pickerHoverElementRef.current;
      if (!hovered) {
        return;
      }

      event.preventDefault();
      event.stopPropagation();
      updatePendingSelectionFromElement(hovered, "picker");

      setPickerActive(false);
    };

    const onKeyDown = (event: KeyboardEvent) => {
      if (event.key === "Escape") {
        // Handled here, so a hosting side panel stays open (F-215).
        event.preventDefault();
        setPickerActive(false);
      }
    };

    const onContextMenu = (event: MouseEvent) => {
      if (pickerGuidanceDismissed) {
        return;
      }

      event.preventDefault();
      event.stopPropagation();
      setPickerGuidanceDismissed(true);
    };

    document.addEventListener("mousemove", onMouseMove, true);
    document.addEventListener("click", onClick, true);
    document.addEventListener("keydown", onKeyDown, true);
    document.addEventListener("contextmenu", onContextMenu, true);

    return () => {
      document.removeEventListener("mousemove", onMouseMove, true);
      document.removeEventListener("click", onClick, true);
      document.removeEventListener("keydown", onKeyDown, true);
      document.removeEventListener("contextmenu", onContextMenu, true);
    };
  }, [
    draftState.category,
    isOpen,
    location.pathname,
    pickerActive,
    pickerGuidanceDismissed,
    updatePendingSelectionFromElement,
  ]);

  useEffect(() => {
    if (!isOpen) return;
    if (!elementSelection) {
      latestSuggestionRequestRef.current += 1;
      latestSuggestionKeyRef.current = null;
      latestSuggestionStartedAtRef.current = null;
      setBugSuggestionState({
        status: "idle",
        reason: null,
        suggestions: [],
        topConfidence: null,
      });
      return;
    }

    if (draftState.category !== "bug") {
      latestSuggestionRequestRef.current += 1;
      latestSuggestionKeyRef.current = null;
      latestSuggestionStartedAtRef.current = null;
      setBugSuggestionState({
        status: "ready",
        reason: null,
        suggestions: [],
        topConfidence: null,
      });
      return;
    }

    const suggestionKey = JSON.stringify({
      category: draftState.category,
      selector: elementSelection.selector,
      tagName: elementSelection.tagName,
      path: sanitizedPathname,
      search: buildAllowedIssueReportSearch(location.search),
    });

    if (latestSuggestionKeyRef.current === suggestionKey) {
      return;
    }

    latestSuggestionKeyRef.current = suggestionKey;

    const requestId = latestSuggestionRequestRef.current + 1;
    latestSuggestionRequestRef.current = requestId;
    latestSuggestionStartedAtRef.current = Date.now();
    // Keep the form locked until suggestion generation settles so the
    // operator does not start editing against a stale selection and then
    // lose the generated draft copy when the async result lands.
    setBugSuggestionState({
      status: "loading",
      reason: null,
      suggestions: [],
      topConfidence: null,
    });

    const currentUrl =
      currentAdminUrl ||
      buildIssueReportUrl(location.pathname, location.search);
    const diagnosticsDigest = buildDiagnosticsDigest(
      buildFrontendDiagnosticsSnapshot(),
    );

    logger.info("Issue report bug suggestions started", {
      operation: "issue_report_bug_suggestions_started",
      category: draftState.category,
      route: sanitizedPathname,
      elementTagName: elementSelection.tagName,
      elementRole: elementSelection.role,
      elementDisplay: elementSelection.display,
      htmlSnapshotLength: elementSelection.selectedHtml?.length ?? 0,
      visibleTextLength: elementSelection.visibleText?.length ?? 0,
      nearbyTextCount: elementSelection.nearbyTextContext.length,
      headingCount: elementSelection.headings.length,
      actionLabelCount: elementSelection.actionLabels.length,
      statusTextCount: elementSelection.statusText.length,
      hasParentContext: Boolean(elementSelection.parentContext),
      parentHtmlSnapshotLength:
        elementSelection.parentContext?.selectedHtml?.length ?? 0,
      diagnosticsDigestRecordCount:
        diagnosticsDigest.notableEvents.length +
        diagnosticsDigest.failedRequests.length,
    });

    void generateBugSuggestions({
      category: draftState.category,
      adminUrl: currentUrl,
      routeHint: normalizeText(draftState.routeHint, 500) || undefined,
      pageTitle:
        typeof document !== "undefined"
          ? normalizeText(document.title, 200) || undefined
          : undefined,
      clientContext: {
        capturedAt: new Date().toISOString(),
        timezone: Intl.DateTimeFormat().resolvedOptions().timeZone || "Unknown",
        viewport: {
          width: typeof window === "undefined" ? 0 : window.innerWidth,
          height: typeof window === "undefined" ? 0 : window.innerHeight,
          devicePixelRatio:
            typeof window === "undefined" ? 1 : window.devicePixelRatio,
        },
        userAgent:
          typeof window === "undefined"
            ? "Unavailable"
            : window.navigator.userAgent,
        currentPath: sanitizedPathname,
        ...(buildAllowedIssueReportSearch(location.search)
          ? {
              currentSearch: buildAllowedIssueReportSearch(location.search),
            }
          : {}),
      },
      element: buildIssueReportSuggestionElement(elementSelection),
      diagnosticsDigest: serializeJsonValue(diagnosticsDigest),
    })
      .unwrap()
      .then((response) => {
        if (latestSuggestionRequestRef.current !== requestId) {
          return;
        }

        const result = response.bugSuggestionResult;
        const durationMs =
          latestSuggestionStartedAtRef.current === null
            ? null
            : Date.now() - latestSuggestionStartedAtRef.current;
        logger.info("Issue report bug suggestions completed", {
          operation: "issue_report_bug_suggestions_completed",
          category: draftState.category,
          route: sanitizedPathname,
          suggestionCount: result.suggestions.length,
          topConfidence: result.topConfidence,
          decision: result.reason,
          durationMs,
          slowNoticeShown:
            durationMs !== null && durationMs >= BUG_SUGGESTION_SLOW_NOTICE_MS,
        });
        latestSuggestionStartedAtRef.current = null;

        if (result.shouldPrompt && result.suggestions.length > 0) {
          setBugSuggestionState({
            status: "selecting",
            reason: result.reason,
            suggestions: result.suggestions,
            topConfidence: result.topConfidence,
          });
          return;
        }

        setBugSuggestionState({
          status: "ready",
          reason: result.reason,
          suggestions: [],
          topConfidence: result.topConfidence,
        });
      })
      .catch((error) => {
        if (latestSuggestionRequestRef.current !== requestId) {
          return;
        }

        const durationMs =
          latestSuggestionStartedAtRef.current === null
            ? null
            : Date.now() - latestSuggestionStartedAtRef.current;
        logger.warn("Issue report bug suggestions failed", {
          operation: "issue_report_bug_suggestions_failed",
          category: draftState.category,
          route: sanitizedPathname,
          errorMessage: getErrorMessage(error),
          durationMs,
        });
        latestSuggestionStartedAtRef.current = null;

        setBugSuggestionState({
          status: "ready",
          reason: "unavailable",
          suggestions: [],
          topConfidence: null,
        });
      });
  }, [
    currentAdminUrl,
    draftState.category,
    draftState.routeHint,
    elementSelection,
    generateBugSuggestions,
    isOpen,
    location.pathname,
    location.search,
    sanitizedPathname,
  ]);

  useEffect(() => {
    if (!isOpen || !pendingElementSelection) {
      return;
    }

    const onKeyDown = (event: KeyboardEvent) => {
      if (event.key === "Escape") {
        event.preventDefault();
        logger.info("Issue report element selection cancelled", {
          operation: "issue_report_picker_selection_cancelled",
          category: draftState.category,
          route: sanitizedPathname,
          elementTagName: pendingElementSelection.tagName,
        });
        setPendingElementSelection(null);
        setPendingBroaderElementSelection(null);
        pendingBroaderElementRef.current = null;
        setElementSelection(null);
        clearValidationProgress();
      }
    };

    document.addEventListener("keydown", onKeyDown, true);
    return () => {
      document.removeEventListener("keydown", onKeyDown, true);
    };
  }, [
    clearValidationProgress,
    draftState.category,
    isOpen,
    pendingElementSelection,
    sanitizedPathname,
  ]);

  const handleScreenshotFile = async (
    file: File,
    source: "upload" | "paste",
  ) => {
    setUiError(null);
    try {
      const resized: ResizeImageToPngResult = await resizeImageFileToPngDataUrl(
        file,
        {
          maxPngBytes: 1_500_000,
        },
      );

      setScreenshotState({
        dataUrl: resized.dataUrl,
        byteSize: resized.byteSize,
        fileName: file.name || "screenshot.png",
        source,
      });
      setAnnotatedDataUrl(resized.dataUrl);
      setAnnotatedByteSize(resized.byteSize);
    } catch {
      setUiError("Failed to process screenshot.");
    }
  };

  const onPasteScreenshot = (event: ReactClipboardEvent) => {
    const file = selectImageFileFromClipboard(event.clipboardData);
    if (!file) return;

    event.preventDefault();
    void handleScreenshotFile(file, "paste");
  };

  const validateStepLoading =
    createDraftState.isLoading || validateDraftState.isLoading;

  const isSubmitting = submitIssueReportState.isLoading;

  const hasFollowUpAnswer = Object.values(followUpAnswers).some(
    (answer) => answer.trim().length > 0,
  );
  const requiresFollowUpAnswer = Boolean(
    validationResult && validationResult.followUpQuestions.length > 0,
  );
  const hasElementSelection = elementSelection !== null;
  const hasPendingElementSelection = pendingElementSelection !== null;
  const isBugCategory = draftState.category === "bug";
  const isFeedbackCategory = draftState.category === "feedback";
  const isSuggestionLoading =
    isBugCategory && bugSuggestionState.status === "loading";
  const isSuggestionDialogOpen =
    isBugCategory &&
    bugSuggestionState.status === "selecting" &&
    bugSuggestionState.suggestions.length > 0;
  // Bug reports deliberately force a picker -> suggestion -> edit sequence so
  // the draft starts from the same evidence package that later validation uses.
  // Keep the two steps explicit for operators; see docs/features/BUG_TRACKER.md
  // and docs/guides/PLAIN_LANGUAGE.md.
  const canEditReportDetails =
    isFeedbackCategory ||
    (hasElementSelection &&
      (!isBugCategory || bugSuggestionState.status === "ready"));
  const viewportWidth =
    typeof window === "undefined" ? PICKER_HINT_WIDTH + 16 : window.innerWidth;
  const viewportHeight =
    typeof window === "undefined"
      ? PICKER_HINT_HEIGHT + 16
      : window.innerHeight;
  const pendingSelectionAdvisories = pendingElementSelection
    ? buildPickerSelectionAdvisories(
        pendingElementSelection.rect,
        viewportWidth,
        viewportHeight,
      )
    : [];
  const pendingSelectionPreviewLines = pendingElementSelection
    ? [
        pendingElementSelection.label,
        ...pendingElementSelection.nearbyTextContext.filter(
          (line) => line !== pendingElementSelection.label,
        ),
      ].slice(0, 3)
    : [];
  const pendingSelectionCoverage = pendingElementSelection
    ? ((pendingElementSelection.rect.width *
        pendingElementSelection.rect.height) /
        Math.max(viewportWidth * viewportHeight, 1)) *
      100
    : null;
  const broaderSelectionPreviewRect =
    isPreviewingBroaderElementSelection && pendingBroaderElementSelection
      ? pendingBroaderElementSelection.rect
      : null;
  const currentSelectionAdvisories = elementSelection
    ? buildPickerSelectionAdvisories(
        elementSelection.rect,
        viewportWidth,
        viewportHeight,
      )
    : [];
  const pendingSelectionLooksSmall = pendingSelectionAdvisories.some(
    (advisory) => advisory.code === "too_small",
  );
  const currentSelectionLooksSmall = currentSelectionAdvisories.some(
    (advisory) => advisory.code === "too_small",
  );
  const insufficientEvidenceFallbackMessage = currentSelectionLooksSmall
    ? "This selection did not provide enough evidence for a draft suggestion. You can write the bug details yourself, or try picking a slightly larger area around where the bug appears."
    : "This selection did not provide enough evidence for a draft suggestion. You can write the bug details yourself or re-pick the area if a nearby panel would describe the bug better.";
  const timeoutFallbackMessage = currentSelectionLooksSmall
    ? "Suggestions timed out, so manual entry has been unlocked. If this area is only part of the bug, try picking a slightly larger section first."
    : "Suggestions timed out, so manual entry has been unlocked. You can continue manually or re-pick the area if a broader section would describe the bug better.";
  const unavailableFallbackMessage = currentSelectionLooksSmall
    ? "Suggestions are unavailable right now, so you can enter the bug details manually. If needed, try selecting a slightly larger area around the problem."
    : "Suggestions are unavailable right now, so you can enter the bug details manually or re-pick a broader section if that would better capture the bug.";
  const buildIssueReportDraftPayload = () => {
    const currentUrl =
      currentAdminUrl ||
      buildIssueReportUrl(location.pathname, location.search);
    const userAgent = window.navigator.userAgent;
    const timezone =
      Intl.DateTimeFormat().resolvedOptions().timeZone || "Unknown";
    const frontendDiagnostics = buildFrontendDiagnosticsSnapshot();
    const diagnosticsDigest = buildDiagnosticsDigest(frontendDiagnostics);

    return {
      site: "admin" as const,
      category: draftState.category,
      priority: draftState.priority,
      tumu: draftState.tumu,
      title: normalizeText(draftState.title, 220),
      description: normalizeText(draftState.description, 12_000) || undefined,
      adminUrl: currentUrl,
      routeHint: normalizeText(draftState.routeHint, 500) || undefined,
      ...(draftState.category === "bug"
        ? {
            // Bug reports carry the recent frontend diagnostics buffer, so the
            // app logs leading up to the report become part of the submitted evidence.
            frontendDiagnostics: serializeJsonValue(frontendDiagnostics),
          }
        : {}),
      clientContext: serializeJsonValue({
        capturedAt: new Date().toISOString(),
        timezone,
        viewport: {
          width: window.innerWidth,
          height: window.innerHeight,
          devicePixelRatio: window.devicePixelRatio,
        },
        userAgent,
        currentPath: sanitizedPathname,
        ...(buildAllowedIssueReportSearch(location.search)
          ? {
              currentSearch: buildAllowedIssueReportSearch(location.search),
            }
          : {}),
        selectionContext: buildIssueReportSelectionContext(elementSelection),
        diagnosticsDigest:
          draftState.category === "bug"
            ? serializeJsonValue(diagnosticsDigest)
            : null,
      }),
      ...(elementSelection
        ? {
            ...(isIssueReportDebugCaptureEnabled()
              ? { elementSelector: elementSelection.selector }
              : {}),
            elementRect: elementSelection.rect,
            elementLabel: buildIssueReportElementLabel(
              elementSelection,
              isIssueReportDebugCaptureEnabled(),
            ),
          }
        : {}),
      ...(annotatedDataUrl
        ? {
            screenshotPngDataUrl: annotatedDataUrl,
            screenshotFileName:
              screenshotState?.fileName ?? "annotated-screenshot.png",
          }
        : {}),
    };
  };
  const pendingSelectionCardPosition = pendingElementSelection
    ? buildPickerConfirmationCardPosition(
        pendingElementSelection.rect,
        viewportWidth,
        viewportHeight,
      )
    : null;
  const pickerOverlayRect =
    broaderSelectionPreviewRect ??
    pendingElementSelection?.rect ??
    (pickerActive ? pickerHoverRect : null);
  const clientContextPreview = {
    capturedAt: new Date().toISOString(),
    timezone: Intl.DateTimeFormat().resolvedOptions().timeZone,
    viewport: {
      width: typeof window === "undefined" ? 0 : window.innerWidth,
      height: typeof window === "undefined" ? 0 : window.innerHeight,
      devicePixelRatio:
        typeof window === "undefined" ? 1 : window.devicePixelRatio,
    },
    userAgent:
      typeof window === "undefined"
        ? "Unavailable"
        : window.navigator.userAgent,
    currentPath: sanitizedPathname,
    ...(buildAllowedIssueReportSearch(location.search)
      ? {
          currentSearch: buildAllowedIssueReportSearch(location.search),
        }
      : {}),
    selectionContext: buildIssueReportSelectionContext(elementSelection),
    diagnosticsDigest:
      draftState.category === "bug"
        ? buildDiagnosticsDigest(
            developerDiagnosticsSnapshot ?? buildFrontendDiagnosticsSnapshot(),
          )
        : null,
  };
  const shouldAttachFrontendDiagnostics = draftState.category === "bug";
  const diagnosticsPreview = shouldAttachFrontendDiagnostics
    ? (developerDiagnosticsSnapshot ?? buildFrontendDiagnosticsSnapshot())
    : null;
  const screenshotPreview = screenshotState
    ? {
        source: screenshotState.source,
        fileName: screenshotState.fileName,
        originalByteSize: screenshotState.byteSize,
        attachedByteSize: annotatedByteSize ?? screenshotState.byteSize,
        exceedsLimit:
          (annotatedByteSize ?? screenshotState.byteSize) >
          MAX_SCREENSHOT_BYTES,
      }
    : null;

  const handleConfirmElementSelection = () => {
    if (!pendingElementSelection) {
      return;
    }

    setElementSelection(pendingElementSelection);
    setPendingElementSelection(null);
    setPendingBroaderElementSelection(null);
    setIsPreviewingBroaderElementSelection(false);
    pendingBroaderElementRef.current = null;
    clearValidationProgress();
    logger.info("Issue report element selection confirmed", {
      operation: "issue_report_picker_selection_confirmed",
      category: draftState.category,
      route: sanitizedPathname,
      elementTagName: pendingElementSelection.tagName,
      elementRole: pendingElementSelection.role,
      elementDisplay: pendingElementSelection.display,
      advisoryCodes: pendingSelectionAdvisories.map(
        (advisory) => advisory.code,
      ),
    });
  };

  const handleRetryElementSelection = () => {
    if (pendingElementSelection) {
      logger.info("Issue report element selection retried", {
        operation: "issue_report_picker_selection_retry",
        category: draftState.category,
        route: sanitizedPathname,
        elementTagName: pendingElementSelection.tagName,
        advisoryCodes: pendingSelectionAdvisories.map(
          (advisory) => advisory.code,
        ),
      });
    }

    setPendingElementSelection(null);
    setPendingBroaderElementSelection(null);
    setIsPreviewingBroaderElementSelection(false);
    pendingBroaderElementRef.current = null;
    setElementSelection(null);
    clearValidationProgress();
    setPickerActive(true);
  };

  const handleCancelPendingElementSelection = () => {
    if (pendingElementSelection) {
      logger.info("Issue report element selection cancelled", {
        operation: "issue_report_picker_selection_cancelled",
        category: draftState.category,
        route: sanitizedPathname,
        elementTagName: pendingElementSelection.tagName,
      });
    }

    setPendingElementSelection(null);
    setPendingBroaderElementSelection(null);
    setIsPreviewingBroaderElementSelection(false);
    pendingBroaderElementRef.current = null;
    setElementSelection(null);
    clearValidationProgress();
  };

  const handleUseBroaderElementSelection = () => {
    if (!pendingBroaderElementRef.current) {
      return;
    }

    updatePendingSelectionFromElement(
      pendingBroaderElementRef.current,
      "broadened",
    );
  };

  const handleOpenDeveloperContext = () => {
    logger.debug("Admin issue widget developer context opened", {
      operation: "issue_report_developer_context_opened",
      route: sanitizedPathname,
      issueReportId,
      category: draftState.category,
      hasValidationResult: Boolean(validationResult),
      hasScreenshot: Boolean(screenshotState),
    });
    setDeveloperDiagnosticsSnapshot(buildFrontendDiagnosticsSnapshot());
    setIsDeveloperContextOpen(true);
  };
  const developerPayloadPreview = {
    category: draftState.category,
    title: normalizeText(draftState.title, 220) || null,
    description: normalizeText(draftState.description, 12_000) || null,
    adminUrl:
      currentAdminUrl ||
      buildIssueReportUrl(location.pathname, location.search),
    routeHint: normalizeText(draftState.routeHint, 500) || null,
    elementSelection: elementSelection
      ? {
          label: buildIssueReportElementLabel(
            elementSelection,
            isIssueReportDebugCaptureEnabled(),
          ),
          rect: elementSelection.rect,
          ...(isIssueReportDebugCaptureEnabled()
            ? { selector: elementSelection.selector }
            : {}),
        }
      : null,
    screenshot: screenshotPreview,
    clientContext: clientContextPreview,
    frontendDiagnostics: diagnosticsPreview,
  };

  const handleUseBugSuggestion = (
    suggestion: AdminV2IssueReportBugSuggestion,
    index: number,
  ) => {
    setDraftState((previous) => ({
      ...previous,
      title: suggestion.title,
      description: suggestion.description,
    }));
    // Once a human accepts the draft, the next trust boundary is validate:
    // the operator can edit the copy, so we stop showing the provisional
    // review state in the main form.
    setBugSuggestionState((previous) => ({
      ...previous,
      status: "ready",
      reason: "accepted",
    }));
    logger.info("Issue report bug suggestion selected", {
      operation: "issue_report_bug_suggestion_selected",
      category: draftState.category,
      route: sanitizedPathname,
      suggestionIndex: index,
      topConfidence: bugSuggestionState.topConfidence,
      decisionAtSelection: bugSuggestionState.reason,
    });
  };

  const handleUseManualBugEntry = () => {
    setBugSuggestionState((previous) => ({
      ...previous,
      status: "ready",
      reason: "manual",
    }));
    logger.info("Issue report bug suggestion skipped", {
      operation: "issue_report_bug_suggestion_manual",
      category: draftState.category,
      route: sanitizedPathname,
      suggestionCount: bugSuggestionState.suggestions.length,
    });
  };

  const onValidate = async () => {
    if (!hasElementSelection) {
      logger.debug("Admin issue report validation blocked: element required", {
        operation: "issue_report_validate_blocked_no_selection",
        route: sanitizedPathname,
        category: draftState.category,
      });
      setUiError("Pick an element before validating this issue report.");
      return;
    }

    const normalizedTitle = normalizeText(draftState.title, 220);
    if (!normalizedTitle) {
      logger.debug("Admin issue report validation blocked: title required", {
        operation: "issue_report_validate_blocked_missing_title",
        route: sanitizedPathname,
        category: draftState.category,
        hasElementSelection,
      });
      setUiError("Title is required.");
      return;
    }

    const screenshotByteSize =
      annotatedByteSize ?? screenshotState?.byteSize ?? null;
    if (
      annotatedDataUrl &&
      screenshotByteSize !== null &&
      screenshotByteSize > MAX_SCREENSHOT_BYTES
    ) {
      logger.debug(
        "Admin issue report validation blocked: screenshot too large",
        {
          operation: "issue_report_validate_blocked_large_screenshot",
          route: sanitizedPathname,
          category: draftState.category,
          screenshotByteSize,
          maxScreenshotBytes: MAX_SCREENSHOT_BYTES,
        },
      );
      setUiError(
        `Screenshot is too large (${Math.round(
          screenshotByteSize / 1024,
        )} KB). Please crop or re-upload a smaller image.`,
      );
      return;
    }

    setUiError(null);
    logger.info("Admin issue report validation started", {
      operation: "issue_report_validate_started",
      route: sanitizedPathname,
      category: draftState.category,
      hasScreenshot: Boolean(annotatedDataUrl),
      hasExistingDraft: Boolean(issueReportId),
      selectedElementTag: elementSelection.tagName,
      selectedElementRole: elementSelection.role,
    });

    try {
      const validationDraft = await createDraft({
        ...buildIssueReportDraftPayload(),
        title: normalizedTitle,
      }).unwrap();

      setIssueReportId(validationDraft.issueReport.id);
      logger.info("Admin issue report validation draft created", {
        operation: "issue_report_validate_draft_created",
        route: sanitizedPathname,
        category: draftState.category,
        issueReportId: validationDraft.issueReport.id,
      });

      const validation = await validateDraft({
        issueReportId: validationDraft.issueReport.id,
      }).unwrap();

      setValidationResult(validation.validationResult);
      setFollowUpAnswers(
        Object.fromEntries(
          validation.validationResult.followUpQuestions.map((question) => [
            question.id,
            "",
          ]),
        ),
      );
      logger.info("Admin issue report validation completed", {
        operation: "issue_report_validate_completed",
        route: sanitizedPathname,
        category: draftState.category,
        issueReportId: validationDraft.issueReport.id,
        followUpQuestionCount:
          validation.validationResult.followUpQuestions.length,
        qualityNoteCount: validation.validationResult.qualityNotes.length,
      });
    } catch (error) {
      logger.warn("Admin issue report validation failed", {
        operation: "issue_report_validate_failed",
        route: sanitizedPathname,
        category: draftState.category,
        issueReportId,
        error: getErrorMessage(error),
      });
      setUiError(getErrorMessage(error) || "Validate failed.");
    }
  };

  const onSubmit = async () => {
    if (!isFeedbackCategory && (!validationResult || !issueReportId)) {
      logger.debug("Admin issue report submit blocked: validation required", {
        operation: "issue_report_submit_blocked_validation_required",
        route: sanitizedPathname,
        category: draftState.category,
        issueReportId,
        hasValidationResult: Boolean(validationResult),
      });
      setUiError("Validate this issue report before submitting.");
      return;
    }

    const normalizedTitle = normalizeText(draftState.title, 220);
    if (!normalizedTitle) {
      logger.debug("Admin issue report submit blocked: title required", {
        operation: "issue_report_submit_blocked_missing_title",
        route: sanitizedPathname,
        category: draftState.category,
        issueReportId,
      });
      setUiError("Title is required.");
      return;
    }

    let currentIssueReportId = issueReportId;
    if (isFeedbackCategory && !currentIssueReportId) {
      try {
        logger.info("Admin issue report feedback draft creation started", {
          operation: "issue_report_feedback_draft_started",
          route: sanitizedPathname,
          category: draftState.category,
        });
        const draftResponse = await createDraft({
          ...buildIssueReportDraftPayload(),
          title: normalizedTitle,
        }).unwrap();
        currentIssueReportId = draftResponse.issueReport.id;
        setIssueReportId(currentIssueReportId);
        logger.info("Admin issue report feedback draft created", {
          operation: "issue_report_feedback_draft_created",
          route: sanitizedPathname,
          category: draftState.category,
          issueReportId: currentIssueReportId,
        });
      } catch (error) {
        logger.warn("Admin issue report feedback draft failed", {
          operation: "issue_report_feedback_draft_failed",
          route: sanitizedPathname,
          category: draftState.category,
          error: getErrorMessage(error),
        });
        setUiError(getErrorMessage(error) || "Failed to save feedback.");
        return;
      }
    }

    if (!currentIssueReportId) {
      logger.warn("Admin issue report submit blocked: draft missing", {
        operation: "issue_report_submit_blocked_missing_draft",
        route: sanitizedPathname,
        category: draftState.category,
      });
      setUiError("Issue report draft is missing.");
      return;
    }

    const followUpAnswersPayload: AdminV2IssueReportFollowUpAnswer[] = (
      validationResult?.followUpQuestions ?? []
    )
      .map((question) => ({
        id: question.id,
        answer: normalizeText(followUpAnswers[question.id] ?? "", 4_000),
      }))
      .filter((answer) => answer.answer.length > 0);

    if (requiresFollowUpAnswer && followUpAnswersPayload.length < 1) {
      logger.debug(
        "Admin issue report submit blocked: follow-up answer required",
        {
          operation: "issue_report_submit_blocked_followup_required",
          route: sanitizedPathname,
          category: draftState.category,
          issueReportId: currentIssueReportId,
          followUpQuestionCount:
            validationResult?.followUpQuestions.length ?? 0,
        },
      );
      setUiError("At least one follow-up answer is required before submit.");
      return;
    }

    setUiError(null);
    logger.info("Admin issue report submit started", {
      operation: "issue_report_submit_started",
      route: sanitizedPathname,
      category: draftState.category,
      issueReportId: currentIssueReportId,
      followUpAnswerCount: followUpAnswersPayload.length,
      hasAdditionalContext: additionalContext.trim().length > 0,
    });

    try {
      const response = await submitIssueReport({
        issueReportId: currentIssueReportId,
        followUpAnswers: followUpAnswersPayload,
        additionalContext: normalizeText(additionalContext, 2_000) || undefined,
      }).unwrap();

      const submittedId = response.issueReport.id;
      logger.info("Admin issue report submitted", {
        operation: "issue_report_submit_completed",
        route: sanitizedPathname,
        category: draftState.category,
        issueReportId: submittedId,
      });
      resetPanel();
      closeWidget();
      void requestBroadcastExit(() =>
        navigate(`/issue-reports/${submittedId}`),
      );
    } catch (error) {
      logger.warn("Admin issue report submit failed", {
        operation: "issue_report_submit_failed",
        route: sanitizedPathname,
        category: draftState.category,
        issueReportId: currentIssueReportId,
        error: getErrorMessage(error),
      });
      setUiError(getErrorMessage(error) || "Submit failed.");
    }
  };

  if (!isOpen) {
    if (hosted) return null;
    return (
      <Box
        data-hc-admin-issue-widget-root
        sx={{
          position: "fixed",
          right: { xs: 12, md: 24 },
          // Stack above the AI assistant trigger with a 10px vertical gap.
          bottom: { xs: 88, md: 100 },
          zIndex: (theme) => theme.zIndex.modal - 1,
        }}
      >
        <Button
          variant="contained"
          color="secondary"
          startIcon={<BugReportOutlinedIcon />}
          onClick={() => {
            logger.debug("Admin issue widget opened", {
              operation: "issue_report_widget_opened",
              route: sanitizedPathname,
            });
            resetPanel();
            setIsOpen(true);
          }}
          aria-label="Open admin issue widget"
        >
          {ISSUE_WIDGET_TRIGGER_LABEL}
        </Button>
      </Box>
    );
  }

  return (
    <Paper
      ref={widgetPaperRef}
      data-hc-admin-issue-widget-root
      elevation={hosted ? 0 : 12}
      onPaste={onPasteScreenshot}
      style={
        widgetPosition && !isMobile && !hosted
          ? {
              left: `${widgetPosition.left}px`,
              top: `${widgetPosition.top}px`,
              right: "auto",
              bottom: "auto",
            }
          : undefined
      }
      sx={{
        position: "fixed",
        right: widgetPosition && !isMobile ? "auto" : { xs: 12, md: 24 },
        bottom: widgetPosition && !isMobile ? "auto" : { xs: 68, md: 80 },
        zIndex: (theme) => theme.zIndex.modal - 1,
        p: `${ISSUE_WIDGET_SHELL_PADDING_PX}px`,
        width: isMobile ? "calc(100vw - 24px)" : 560,
        maxHeight: isMobile ? "calc(100vh - 24px)" : "calc(100vh - 48px)",
        display: "flex",
        flexDirection: "column",
        borderRadius: 2,
        overflow: "hidden",
        backgroundColor: "background.paper",
        boxShadow: (theme) =>
          `0 22px 52px ${alpha(theme.palette.grey[900], 0.24)}`,
        ...(hosted
          ? {
              position: "relative",
              // Replace (not just inset) the standalone responsive offsets:
              // relative right/bottom would shift the capture inside the panel.
              top: "auto",
              right: "auto",
              bottom: "auto",
              left: "auto",
              width: "100%",
              height: "100%",
              maxHeight: "none",
              borderRadius: 0,
              boxShadow: "none",
              zIndex: "auto",
            }
          : {}),
      }}
    >
      {hosted ? null : (
        <Box
          ref={widgetHeaderRef}
          data-testid="admin-issue-widget-header"
          onPointerDown={handleWidgetHeaderPointerDown}
          onPointerMove={handleWidgetHeaderPointerMove}
          sx={{
            mx: `-${ISSUE_WIDGET_SHELL_PADDING_PX}px`,
            mt: `-${ISSUE_WIDGET_SHELL_PADDING_PX}px`,
            px: `${ISSUE_WIDGET_CONTENT_RAIL_PX}px`,
            py: 1.5,
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
            borderBottom: (theme) =>
              `1px solid ${alpha(theme.palette.common.white, 0.18)}`,
            backgroundColor: ISSUE_WIDGET_HEADER_BACKGROUND,
            color: "common.white",
            cursor: isMobile
              ? "default"
              : isDraggingWidget
                ? "grabbing"
                : "grab",
          }}
        >
          <Stack spacing={0}>
            <Typography variant="subtitle2" sx={{ fontWeight: 700 }}>
              {ISSUE_WIDGET_TITLE}
            </Typography>
            <Typography
              variant="caption"
              sx={{
                color: (theme) => alpha(theme.palette.common.white, 0.74),
              }}
            >
              {ISSUE_WIDGET_SUBTITLE}
            </Typography>
          </Stack>

          <Stack direction="row" spacing={1} sx={{ alignItems: "center" }}>
            <Tooltip
              arrow
              placement="bottom-end"
              title={
                <Stack spacing={0.5} sx={{ maxWidth: 260 }}>
                  <Typography
                    variant="caption"
                    sx={{
                      fontWeight: 700,
                      color: "common.white",
                    }}
                  >
                    Open admin issue queue
                  </Typography>
                  <Typography variant="caption" sx={{ color: "common.white" }}>
                    Jump to the queue of previously logged bugs and issues.
                  </Typography>
                  <Typography
                    variant="caption"
                    sx={{
                      color: "common.white",
                      opacity: 0.92,
                    }}
                  >
                    Review screenshots, follow-ups, and status updates from
                    there.
                  </Typography>
                </Stack>
              }
            >
              <IconButton
                size="small"
                aria-label="Open admin issue queue"
                sx={{
                  transform: "translateX(-20px)",
                  cursor: "pointer",
                }}
                onClick={() => {
                  logger.debug("Admin issue widget navigated to queue", {
                    operation: "issue_report_widget_open_queue",
                    route: sanitizedPathname,
                    issueReportId,
                  });
                  closeWidget();
                  void requestBroadcastExit(() => navigate(ISSUE_QUEUE_ROUTE));
                }}
              >
                <Box
                  sx={{
                    position: "relative",
                    width: 18,
                    height: 18,
                    color: (theme) => alpha(theme.palette.common.white, 0.88),
                  }}
                >
                  <BugReportOutlinedIcon
                    sx={{
                      fontSize: 15,
                      position: "absolute",
                      top: -1,
                      left: -1,
                      opacity: 0.68,
                    }}
                  />
                  <BugReportOutlinedIcon
                    sx={{
                      fontSize: 15,
                      position: "absolute",
                      right: -1,
                      bottom: -1,
                    }}
                  />
                </Box>
              </IconButton>
            </Tooltip>

            <Divider
              orientation="vertical"
              flexItem
              sx={{
                borderColor: (theme) => alpha(theme.palette.common.white, 0.22),
              }}
            />

            <IconButton
              size="small"
              aria-label="Close log bug widget"
              sx={{
                cursor: "pointer",
                color: (theme) => alpha(theme.palette.common.white, 0.88),
              }}
              onClick={() => {
                logger.debug("Admin issue widget closed", {
                  operation: "issue_report_widget_closed",
                  route: sanitizedPathname,
                  issueReportId,
                  category: draftState.category,
                });
                closeWidget();
              }}
            >
              <CloseIcon fontSize="small" />
            </IconButton>
          </Stack>
        </Box>
      )}

      <Box
        data-testid="admin-issue-widget-body"
        sx={{
          px: `${ISSUE_WIDGET_CONTENT_RAIL_PX - ISSUE_WIDGET_SHELL_PADDING_PX}px`,
          py: { xs: 2, md: 2.5 },
          overflowY: "auto",
          display: "flex",
          flexDirection: "column",
          flex: 1,
          minHeight: isMobile ? undefined : ISSUE_WIDGET_BODY_MIN_HEIGHT_PX,
        }}
      >
        <Stack spacing={2} sx={{ flex: 1 }}>
          {uiError ? <Alert severity="error">{uiError}</Alert> : null}

          <Stack spacing={0.75}>
            <Typography
              variant="caption"
              sx={{
                color: "text.secondary",
              }}
            >
              Category
            </Typography>
            <ToggleButtonGroup
              exclusive
              value={draftState.category}
              fullWidth
              size="small"
              onChange={(_event, value) => {
                if (typeof value === "string") {
                  logger.debug("Admin issue category changed", {
                    operation: "issue_report_category_changed",
                    route: sanitizedPathname,
                    previousCategory: draftState.category,
                    nextCategory: value,
                  });
                  clearValidationProgress();
                  setDraftState((previous) => ({
                    ...previous,
                    category: value as AdminV2IssueReportCategory,
                  }));
                }
              }}
              sx={{
                width: "100%",
                "& .MuiToggleButton-root": {
                  flex: 1,
                  textTransform: "none",
                  fontWeight: 500,
                  borderColor: "divider",
                },
                // The 420px side panel cannot fit five joined buttons; wrap
                // them as separate buttons so every category stays visible.
                ...(hosted
                  ? {
                      flexWrap: "wrap",
                      gap: 0.75,
                      "& .MuiToggleButtonGroup-grouped": {
                        flex: "1 1 auto",
                        width: "auto",
                        marginLeft: 0,
                        borderRadius: 1,
                        borderLeft: "1px solid var(--hc-issue-category-border)",
                      },
                    }
                  : {}),
              }}
            >
              {CATEGORY_OPTIONS.map((option) => (
                <ToggleButton
                  key={option.value}
                  value={option.value}
                  sx={{
                    "--hc-issue-category-border":
                      CATEGORY_BUTTON_TONES[option.value].borderColor,
                    backgroundColor:
                      CATEGORY_BUTTON_TONES[option.value].unselectedBackground,
                    color: "common.black",
                    borderColor:
                      CATEGORY_BUTTON_TONES[option.value].borderColor,
                    transition:
                      "background-color 150ms ease, color 150ms ease, border-color 150ms ease, box-shadow 150ms ease",
                    "&:hover": {
                      backgroundColor:
                        CATEGORY_BUTTON_TONES[option.value]
                          .unselectedHoverBackground,
                      borderColor:
                        CATEGORY_BUTTON_TONES[option.value].selectedBackground,
                    },
                    "&.Mui-selected": {
                      backgroundColor:
                        CATEGORY_BUTTON_TONES[option.value].selectedBackground,
                      color: "common.white",
                      borderColor:
                        CATEGORY_BUTTON_TONES[option.value]
                          .selectedHoverBackground,
                      boxShadow: (theme) =>
                        `inset 0 0 0 1px ${alpha(
                          theme.palette.common.white,
                          0.16,
                        )}`,
                      fontWeight: 700,
                      "&:hover": {
                        backgroundColor:
                          CATEGORY_BUTTON_TONES[option.value]
                            .selectedHoverBackground,
                      },
                    },
                  }}
                >
                  <Tooltip
                    arrow
                    placement="top"
                    title={
                      <Stack spacing={0.25} sx={{ maxWidth: 260 }}>
                        <Typography
                          variant="caption"
                          sx={{
                            fontWeight: 700,
                            color: "common.white",
                          }}
                        >
                          {option.hoverTitle}
                        </Typography>
                        <Typography
                          variant="caption"
                          sx={{
                            color: "common.white",
                          }}
                        >
                          {option.hoverBody}
                        </Typography>
                        {option.hoverFootnote ? (
                          <Typography
                            variant="caption"
                            sx={{
                              mt: 0.75,
                              color: "grey.300",
                            }}
                          >
                            {option.hoverFootnote}
                          </Typography>
                        ) : null}
                      </Stack>
                    }
                  >
                    <Box
                      component="span"
                      sx={{
                        display: "inline-flex",
                        alignItems: "center",
                        justifyContent: "center",
                        width: "100%",
                      }}
                    >
                      {option.label}
                    </Box>
                  </Tooltip>
                </ToggleButton>
              ))}
            </ToggleButtonGroup>
            {/* Support conversations use their own transactional creator. */}
            <Typography
              data-testid="admin-issue-widget-lands-in"
              variant="caption"
              sx={{
                color: "text.secondary",
              }}
            >
              Lands in Support › Issue queue
            </Typography>
          </Stack>

          <Stack direction="row" spacing={1.5}>
            <Stack spacing={0.75} sx={{ flex: 1 }}>
              <Typography
                variant="caption"
                sx={{
                  color: "text.secondary",
                }}
              >
                Priority
              </Typography>
              <Select
                size="small"
                fullWidth
                value={draftState.priority}
                onChange={(event) => {
                  const next = event.target.value;
                  logger.debug("Admin issue priority changed", {
                    operation: "issue_report_priority_changed",
                    route: sanitizedPathname,
                    previousPriority: draftState.priority,
                    nextPriority: next,
                  });
                  setDraftState((previous) => ({
                    ...previous,
                    priority: next,
                  }));
                }}
              >
                {PRIORITY_OPTIONS.map((option) => (
                  <MenuItem key={option.value} value={option.value}>
                    {option.label}
                  </MenuItem>
                ))}
              </Select>
            </Stack>
            <Stack spacing={0.75} sx={{ flex: 1 }}>
              <Typography
                variant="caption"
                sx={{
                  color: "text.secondary",
                }}
              >
                Customer stage (optional)
              </Typography>
              <Select
                size="small"
                fullWidth
                displayEmpty
                value={draftState.tumu ?? ""}
                onChange={(event) => {
                  const raw = String(event.target.value);
                  const next: AdminV2IssueReportTumuStage | null =
                    raw === "" ? null : (raw as AdminV2IssueReportTumuStage);
                  logger.debug("Admin issue customer stage changed", {
                    operation: "issue_report_customer_stage_changed",
                    route: sanitizedPathname,
                    previousTumu: draftState.tumu,
                    nextTumu: next,
                  });
                  setDraftState((previous) => ({
                    ...previous,
                    tumu: next,
                  }));
                }}
              >
                <MenuItem value="">
                  <Typography
                    component="span"
                    variant="inherit"
                    sx={{
                      color: "text.secondary",
                    }}
                  >
                    Not specified
                  </Typography>
                </MenuItem>
                {TUMU_OPTIONS.map((option) => (
                  <MenuItem key={option.value} value={option.value}>
                    {option.label}
                  </MenuItem>
                ))}
              </Select>
            </Stack>
          </Stack>

          <Stack spacing={1} sx={{ alignItems: "stretch" }}>
            <Button
              variant={pickerActive ? "contained" : "outlined"}
              size="small"
              fullWidth
              startIcon={<CropFreeIcon />}
              onClick={() => {
                if (pendingElementSelection) {
                  handleRetryElementSelection();
                  return;
                }

                setPickerActive((active) => {
                  const nextActive = !active;
                  logger.debug("Admin issue picker toggled", {
                    operation: "issue_report_picker_toggled",
                    route: sanitizedPathname,
                    category: draftState.category,
                    nextActive,
                    hadConfirmedSelection: elementSelection !== null,
                  });
                  return nextActive;
                });
              }}
            >
              {pickerActive
                ? "Picking element… (Esc to cancel)"
                : pendingElementSelection
                  ? "Try again with a different area"
                  : "Pick element"}
            </Button>

            {elementSelection ? (
              <Chip
                size="small"
                color="success"
                label={`Selected: ${elementSelection.label}`}
                onDelete={() => {
                  logger.debug("Admin issue element selection cleared", {
                    operation: "issue_report_selection_cleared",
                    route: sanitizedPathname,
                    category: draftState.category,
                    selectedElementTag: elementSelection.tagName,
                  });
                  setElementSelection(null);
                  clearValidationProgress();
                }}
                sx={{ alignSelf: "flex-start" }}
              />
            ) : pendingElementSelection ? (
              <Chip
                size="small"
                color="warning"
                label="Selection waiting for confirmation"
                onDelete={handleCancelPendingElementSelection}
                sx={{ alignSelf: "flex-start" }}
              />
            ) : (
              <Typography
                variant="caption"
                sx={{
                  color: "text.secondary",
                }}
              >
                {isFeedbackCategory
                  ? "Pick an element only if it helps illustrate the feedback."
                  : "Pick a broad block-level area first to unlock report details."}
              </Typography>
            )}
          </Stack>

          <Stack direction="row" sx={{ justifyContent: "flex-end" }}>
            <Button
              size="small"
              variant="text"
              color="inherit"
              onClick={handleOpenDeveloperContext}
              sx={{
                textTransform: "none",
                fontSize: "0.75rem",
                px: 0.5,
                py: 0.25,
                minWidth: 0,
                opacity: 0.78,
              }}
            >
              Developer context
            </Button>
          </Stack>

          <Box sx={{ position: "relative" }}>
            <fieldset
              disabled={!canEditReportDetails}
              style={{
                border: 0,
                margin: 0,
                padding: 0,
                minWidth: 0,
                minHeight: isMobile
                  ? undefined
                  : ISSUE_WIDGET_WORKFLOW_PANEL_MIN_HEIGHT_PX,
              }}
            >
              <Stack
                spacing={2}
                sx={{
                  opacity: canEditReportDetails ? 1 : 0.4,
                  transition: "opacity 160ms ease",
                  minHeight: isMobile
                    ? undefined
                    : ISSUE_WIDGET_WORKFLOW_PANEL_MIN_HEIGHT_PX,
                }}
              >
                {isSuggestionLoading ? (
                  <Alert
                    severity="info"
                    icon={<CircularProgress size={16} color="inherit" />}
                  >
                    <Stack spacing={0.5}>
                      <Typography component="span" variant="body2">
                        Preparing bug title and description suggestions from the
                        selected element.
                      </Typography>
                      <Typography
                        component="span"
                        variant="caption"
                        sx={{
                          color: "text.secondary",
                        }}
                      >
                        Next, you can choose a starting draft or write the
                        details yourself.
                      </Typography>
                      {showSuggestionSlowNotice ? (
                        <Typography
                          component="span"
                          variant="caption"
                          sx={{
                            color: "text.secondary",
                          }}
                        >
                          Still working. This can take a few seconds for more
                          detailed selections.
                        </Typography>
                      ) : null}
                    </Stack>
                  </Alert>
                ) : null}

                {isBugCategory &&
                bugSuggestionState.status === "ready" &&
                bugSuggestionState.reason === "insufficient_evidence" ? (
                  <Alert severity="info">
                    {insufficientEvidenceFallbackMessage}
                  </Alert>
                ) : null}

                {isBugCategory &&
                bugSuggestionState.status === "ready" &&
                bugSuggestionState.reason === "timeout" ? (
                  <Alert severity="warning">{timeoutFallbackMessage}</Alert>
                ) : null}

                {isBugCategory &&
                bugSuggestionState.status === "ready" &&
                bugSuggestionState.reason === "unavailable" ? (
                  <Alert severity="warning">{unavailableFallbackMessage}</Alert>
                ) : null}

                <TextField
                  size="small"
                  label="Title"
                  value={draftState.title}
                  onChange={(event) =>
                    setDraftState((previous) => ({
                      ...previous,
                      title: event.target.value,
                    }))
                  }
                />

                <TextField
                  label="Description"
                  multiline
                  minRows={3}
                  maxRows={8}
                  value={draftState.description}
                  onChange={(event) =>
                    setDraftState((previous) => ({
                      ...previous,
                      description: event.target.value,
                    }))
                  }
                />

                <TextField
                  size="small"
                  label="Route"
                  value={draftState.routeHint}
                  helperText="Derived from the current page. Sensitive URL details are not attached."
                  slotProps={{
                    input: {
                      readOnly: true,
                    },
                  }}
                />

                <Alert severity="info">
                  URL saved on submit:{" "}
                  <code>
                    {currentAdminUrl ||
                      buildIssueReportUrl(location.pathname, location.search)}
                  </code>
                </Alert>

                <Divider />

                <Stack spacing={1}>
                  <Button
                    variant="outlined"
                    size="small"
                    fullWidth
                    startIcon={<UploadFileIcon />}
                    onClick={() => fileInputRef.current?.click()}
                  >
                    {screenshotState
                      ? "Replace screenshot"
                      : "Upload screenshot"}
                  </Button>

                  <input
                    ref={fileInputRef}
                    hidden
                    accept="image/*"
                    type="file"
                    onChange={(event) => {
                      const file = event.target.files?.[0];
                      if (!file) return;
                      void handleScreenshotFile(file, "upload");
                      event.target.value = "";
                    }}
                  />

                  <Stack
                    direction="row"
                    spacing={1}
                    sx={{ alignItems: "center", flexWrap: "wrap" }}
                  >
                    {screenshotState ? (
                      <Chip
                        size="small"
                        label={`${screenshotState.source} • ${Math.round(
                          (annotatedByteSize ?? screenshotState.byteSize) /
                            1024,
                        )} KB`}
                        onDelete={() => {
                          setScreenshotState(null);
                          setAnnotatedDataUrl(null);
                          setAnnotatedByteSize(null);
                        }}
                      />
                    ) : (
                      <Typography
                        variant="caption"
                        sx={{
                          color: "text.secondary",
                        }}
                      >
                        Paste a screenshot (Ctrl/Cmd+V) or upload an image.
                        Avoid customer details or login links.
                      </Typography>
                    )}
                  </Stack>
                </Stack>

                {screenshotState ? (
                  <IssueScreenshotAnnotationCanvas
                    key={screenshotState.dataUrl}
                    sourceDataUrl={screenshotState.dataUrl}
                    onChange={({ dataUrl, byteSize }) => {
                      setAnnotatedDataUrl(dataUrl);
                      setAnnotatedByteSize(byteSize);
                    }}
                  />
                ) : null}

                {screenshotState &&
                (annotatedByteSize ?? screenshotState.byteSize) >
                  MAX_SCREENSHOT_BYTES ? (
                  <Alert severity="warning">
                    Screenshot exceeds the{" "}
                    {Math.round(MAX_SCREENSHOT_BYTES / 1024)} KB limit after
                    annotation. Please clear some annotations or upload a
                    smaller image.
                  </Alert>
                ) : null}

                <Divider />

                {isFeedbackCategory ? (
                  <Stack spacing={1}>
                    <Alert severity="info" variant="outlined">
                      Feedback skips validation. Add any context you have, then
                      submit directly when ready.
                    </Alert>
                    <Button
                      variant="contained"
                      color="secondary"
                      startIcon={<DrawIcon />}
                      disabled={isSubmitting || createDraftState.isLoading}
                      onClick={() => {
                        void onSubmit();
                      }}
                    >
                      Submit to issue queue
                    </Button>
                  </Stack>
                ) : !validationResult ? (
                  <Button
                    variant="contained"
                    startIcon={<CheckCircleOutlineIcon />}
                    disabled={validateStepLoading || !canEditReportDetails}
                    onClick={() => {
                      void onValidate();
                    }}
                  >
                    Validate
                  </Button>
                ) : (
                  <Stack spacing={1}>
                    <Alert severity="success">
                      {requiresFollowUpAnswer
                        ? "Validation ready. Answer at least one follow-up before submit."
                        : "Validation ready. Enough information is already captured, so you can submit now."}
                    </Alert>

                    <Typography variant="body2" sx={{ fontWeight: 600 }}>
                      {validationResult.restatedIssue}
                    </Typography>

                    {validationResult.qualityNotes.map((note) => (
                      <Typography
                        key={note}
                        variant="caption"
                        sx={{
                          color: "text.secondary",
                        }}
                      >
                        • {note}
                      </Typography>
                    ))}

                    {validationResult.followUpQuestions.length === 0 ? (
                      <Typography
                        variant="caption"
                        sx={{
                          color: "text.secondary",
                        }}
                      >
                        No follow-up questions are needed this time. Add
                        optional context below if there is anything else worth
                        capturing.
                      </Typography>
                    ) : (
                      validationResult.followUpQuestions.map((question) => (
                        <TextField
                          key={question.id}
                          label={question.question}
                          helperText={question.hint}
                          multiline
                          minRows={2}
                          value={followUpAnswers[question.id] ?? ""}
                          onChange={(event) =>
                            setFollowUpAnswers((previous) => ({
                              ...previous,
                              [question.id]: event.target.value,
                            }))
                          }
                        />
                      ))
                    )}

                    <TextField
                      label="Additional context (optional)"
                      multiline
                      minRows={2}
                      value={additionalContext}
                      onChange={(event) =>
                        setAdditionalContext(event.target.value)
                      }
                    />

                    <Tooltip
                      title={
                        !requiresFollowUpAnswer || hasFollowUpAnswer
                          ? ""
                          : "At least one follow-up answer is required"
                      }
                    >
                      <span>
                        <Button
                          variant="contained"
                          color="secondary"
                          startIcon={<DrawIcon />}
                          disabled={
                            isSubmitting ||
                            (requiresFollowUpAnswer && !hasFollowUpAnswer)
                          }
                          onClick={() => {
                            void onSubmit();
                          }}
                        >
                          Submit to issue queue
                        </Button>
                      </span>
                    </Tooltip>
                  </Stack>
                )}
              </Stack>
            </fieldset>

            {!canEditReportDetails ? (
              <Tooltip
                title={
                  hasPendingElementSelection
                    ? "Confirm the highlighted area or choose Try again before editing."
                    : !hasElementSelection
                      ? "Pick a broad block-level element first to activate the remaining fields."
                      : isSuggestionLoading
                        ? "Wait for suggestions or a no-suggestion result before editing."
                        : isSuggestionDialogOpen
                          ? "Choose a suggested draft or write the details yourself to continue."
                          : "Finish the suggestion step before editing."
                }
              >
                <Box
                  sx={{
                    position: "absolute",
                    inset: 0,
                    borderRadius: 1,
                    backgroundColor: "action.disabledBackground",
                    pointerEvents: "auto",
                    cursor: "not-allowed",
                  }}
                />
              </Tooltip>
            ) : null}
          </Box>
        </Stack>
      </Box>

      <Dialog
        open={isSuggestionDialogOpen}
        fullWidth
        maxWidth="sm"
        onClose={(_event, reason) => {
          if (reason === "backdropClick") {
            return;
          }
        }}
      >
        <DialogTitle sx={{ pb: 1 }}>
          Step 2: choose a starting draft
        </DialogTitle>
        <DialogContent dividers>
          <Stack spacing={1.5}>
            <Typography
              variant="body2"
              sx={{
                color: "text.secondary",
              }}
            >
              The selected area is already saved. Choose a suggested title and
              description as a starting point, or write the bug details
              yourself.
            </Typography>

            <Alert severity="info" variant="outlined">
              This step only decides how to prefill the title and description.
              You can still edit everything before you validate and submit.
            </Alert>

            {bugSuggestionState.reason === "needs_review" &&
            bugSuggestionState.suggestions.length > 0 ? (
              <Alert severity="info" variant="outlined">
                {bugSuggestionState.suggestions.length === 1
                  ? "This suggestion is a useful starting point from the selected area. Review and edit it carefully, or write the details yourself."
                  : "These suggestions are useful starting points from the selected area. Review and edit them carefully, or write the details yourself."}
              </Alert>
            ) : null}

            {bugSuggestionState.suggestions.map((suggestion, index) => (
              <Paper
                key={`${suggestion.title}-${index}`}
                variant="outlined"
                sx={{ p: 1.25 }}
              >
                <Stack spacing={1}>
                  <Box>
                    <Typography
                      variant="caption"
                      sx={{
                        color: "text.secondary",
                      }}
                    >
                      Suggestion {index + 1}
                    </Typography>
                    <Typography variant="subtitle2" sx={{ fontWeight: 700 }}>
                      {suggestion.title}
                    </Typography>
                  </Box>

                  <Typography variant="body2">
                    {suggestion.description}
                  </Typography>

                  <Stack
                    direction="row"
                    sx={{
                      justifyContent: "space-between",
                      alignItems: "center",
                    }}
                  >
                    <Typography
                      variant="caption"
                      sx={{
                        color: "text.secondary",
                      }}
                    >
                      {getSuggestionReviewLabel(bugSuggestionState.reason)}
                    </Typography>
                    <Button
                      size="small"
                      variant="contained"
                      onClick={() => handleUseBugSuggestion(suggestion, index)}
                    >
                      Use this draft
                    </Button>
                  </Stack>
                </Stack>
              </Paper>
            ))}

            <Button
              variant="text"
              color="inherit"
              onClick={handleUseManualBugEntry}
            >
              Write it myself
            </Button>
          </Stack>
        </DialogContent>
      </Dialog>

      <Dialog
        open={isDeveloperContextOpen}
        onClose={() => setIsDeveloperContextOpen(false)}
        fullWidth
        maxWidth="md"
      >
        <DialogTitle sx={{ pb: 1 }}>Developer context preview</DialogTitle>
        <DialogContent dividers>
          <Stack spacing={1.5}>
            <Typography
              variant="body2"
              sx={{
                color: "text.secondary",
              }}
            >
              Read-only preview of ticket context and diagnostics attached
              behind the scenes.
            </Typography>

            <Paper variant="outlined" sx={{ p: 1.25 }}>
              <Typography variant="caption" sx={{ fontWeight: 700 }}>
                Report payload preview
              </Typography>
              <Box
                component="pre"
                sx={{
                  mt: 0.75,
                  mb: 0,
                  p: 1,
                  fontSize: "0.75rem",
                  whiteSpace: "pre-wrap",
                  wordBreak: "break-word",
                  backgroundColor: "background.default",
                  borderRadius: 1,
                  overflowX: "auto",
                }}
              >
                {JSON.stringify(
                  {
                    category: developerPayloadPreview.category,
                    title: developerPayloadPreview.title,
                    description: developerPayloadPreview.description,
                    adminUrl: developerPayloadPreview.adminUrl,
                    routeHint: developerPayloadPreview.routeHint,
                    elementSelection: developerPayloadPreview.elementSelection,
                    screenshot: developerPayloadPreview.screenshot,
                  },
                  null,
                  2,
                )}
              </Box>
            </Paper>

            {developerPayloadPreview.frontendDiagnostics ? (
              <>
                <Paper variant="outlined" sx={{ p: 1.25 }}>
                  <Typography variant="caption" sx={{ fontWeight: 700 }}>
                    Browser context
                  </Typography>
                  <Box
                    component="pre"
                    sx={{
                      mt: 0.75,
                      mb: 0,
                      p: 1,
                      fontSize: "0.75rem",
                      whiteSpace: "pre-wrap",
                      wordBreak: "break-word",
                      backgroundColor: "background.default",
                      borderRadius: 1,
                      overflowX: "auto",
                    }}
                  >
                    {JSON.stringify(
                      developerPayloadPreview.frontendDiagnostics
                        .browserContext,
                      null,
                      2,
                    )}
                  </Box>
                </Paper>

                <Paper variant="outlined" sx={{ p: 1.25 }}>
                  <Typography variant="caption" sx={{ fontWeight: 700 }}>
                    Recent logs attached (
                    {developerPayloadPreview.frontendDiagnostics.records.length}
                    )
                  </Typography>
                  <Box
                    component="pre"
                    sx={{
                      mt: 0.75,
                      mb: 0,
                      p: 1,
                      fontSize: "0.75rem",
                      whiteSpace: "pre-wrap",
                      wordBreak: "break-word",
                      backgroundColor: "background.default",
                      borderRadius: 1,
                      overflowX: "auto",
                      maxHeight: 220,
                      overflowY: "auto",
                    }}
                  >
                    {JSON.stringify(
                      developerPayloadPreview.frontendDiagnostics.records,
                      null,
                      2,
                    )}
                  </Box>
                </Paper>
              </>
            ) : (
              <Alert severity="info">
                Browser context and recent local logs are attached only for bug
                reports.
              </Alert>
            )}

            <Paper variant="outlined" sx={{ p: 1.25 }}>
              <Typography variant="caption" sx={{ fontWeight: 700 }}>
                Client context
              </Typography>
              <Box
                component="pre"
                sx={{
                  mt: 0.75,
                  mb: 0,
                  p: 1,
                  fontSize: "0.75rem",
                  whiteSpace: "pre-wrap",
                  wordBreak: "break-word",
                  backgroundColor: "background.default",
                  borderRadius: 1,
                  overflowX: "auto",
                }}
              >
                {JSON.stringify(developerPayloadPreview.clientContext, null, 2)}
              </Box>
            </Paper>
          </Stack>
        </DialogContent>
      </Dialog>

      {pickerOverlayRect ? (
        <Box
          data-hc-admin-widget-overlay
          sx={{
            position: "fixed",
            top: pickerOverlayRect.top,
            left: pickerOverlayRect.left,
            width: pickerOverlayRect.width,
            height: pickerOverlayRect.height,
            border: broaderSelectionPreviewRect
              ? `2px solid ${ISSUE_WIDGET_SELECTION_BORDER_COLORS.broader}`
              : pendingElementSelection
                ? `2px solid ${ISSUE_WIDGET_SELECTION_BORDER_COLORS.pending}`
                : `2px solid ${ISSUE_WIDGET_SELECTION_BORDER_COLORS.active}`,
            borderRadius: 0.5,
            pointerEvents: "none",
            zIndex: (theme) => theme.zIndex.modal + 3,
            boxShadow: broaderSelectionPreviewRect
              ? `0 0 0 9999px ${alpha(
                  ISSUE_WIDGET_SELECTION_BORDER_COLORS.broader,
                  0.12,
                )}`
              : pendingElementSelection
                ? (theme) =>
                    `0 0 0 9999px ${alpha(theme.palette.grey[900], 0.14)}`
                : (theme) =>
                    `0 0 0 9999px ${alpha(theme.palette.grey[900], 0.2)}`,
          }}
        />
      ) : null}

      {pendingElementSelection && pendingSelectionCardPosition ? (
        <Box
          data-hc-admin-picker-confirmation-layer
          sx={{
            position: "fixed",
            inset: 0,
            zIndex: (theme) => theme.zIndex.modal + 4,
          }}
        >
          <Paper
            role="dialog"
            aria-modal="true"
            aria-labelledby="issue-picker-confirm-title"
            elevation={8}
            sx={{
              position: "fixed",
              top: pendingSelectionCardPosition.top,
              left: pendingSelectionCardPosition.left,
              width: pendingSelectionCardPosition.width,
              maxHeight: pendingSelectionCardPosition.maxHeight,
              overflowY: "auto",
              p: 1.5,
              borderRadius: 2,
              border: (theme) => `1px solid ${theme.palette.warning.light}`,
              boxShadow: theme.shadows[10],
            }}
          >
            <Stack spacing={1.25}>
              <Stack
                direction="row"
                spacing={1}
                sx={{
                  alignItems: "flex-start",
                  justifyContent: "space-between",
                }}
              >
                <Box>
                  <Typography
                    id="issue-picker-confirm-title"
                    variant="subtitle1"
                    sx={{ fontWeight: 700 }}
                  >
                    Use this area?
                  </Typography>
                  <Typography
                    variant="body2"
                    sx={{
                      color: "text.secondary",
                      mt: 0.25,
                    }}
                  >
                    Make sure the outline surrounds the area where you are
                    seeing the bug. Avoid selecting just a title or nearby
                    element unless that is the part that is broken. After you
                    continue, you can choose a suggested draft or write the
                    details yourself.
                  </Typography>
                </Box>

                <IconButton
                  size="small"
                  aria-label="Cancel element selection"
                  onClick={handleCancelPendingElementSelection}
                  sx={{ mt: -0.25, mr: -0.25 }}
                >
                  <CloseIcon fontSize="small" />
                </IconButton>
              </Stack>

              <Paper
                variant="outlined"
                sx={{
                  p: 1.25,
                  borderStyle: "dashed",
                  backgroundColor: "background.default",
                }}
              >
                <Stack spacing={0.75}>
                  <Typography
                    variant="caption"
                    sx={{
                      color: "text.secondary",
                      fontWeight: 700,
                    }}
                  >
                    Selected area preview
                  </Typography>

                  <Stack
                    direction="row"
                    spacing={0.75}
                    useFlexGap
                    sx={{ flexWrap: "wrap" }}
                  >
                    <Chip
                      size="small"
                      label={pendingElementSelection.tagName}
                    />
                    <Chip
                      size="small"
                      label={`${Math.round(
                        pendingElementSelection.rect.width,
                      )} × ${Math.round(
                        pendingElementSelection.rect.height,
                      )} px`}
                    />
                    {pendingSelectionCoverage !== null ? (
                      <Chip
                        size="small"
                        label={`~${
                          pendingSelectionCoverage < 1
                            ? "<1"
                            : Math.round(pendingSelectionCoverage)
                        }% of visible page`}
                      />
                    ) : null}
                  </Stack>

                  {pendingSelectionPreviewLines.map((line) => (
                    <Typography
                      key={line}
                      variant="body2"
                      sx={{ lineHeight: 1.45 }}
                    >
                      {line}
                    </Typography>
                  ))}
                </Stack>
              </Paper>

              {pendingSelectionAdvisories.map((advisory) => (
                <Alert
                  key={advisory.code}
                  severity="warning"
                  variant="outlined"
                >
                  {advisory.message}
                </Alert>
              ))}

              <Stack
                direction={{ xs: "column", sm: "row" }}
                spacing={1}
                sx={{ justifyContent: "flex-end" }}
              >
                {pendingSelectionLooksSmall &&
                pendingBroaderElementSelection ? (
                  <Button
                    autoFocus
                    variant="text"
                    color="inherit"
                    onMouseEnter={() =>
                      setIsPreviewingBroaderElementSelection(true)
                    }
                    onMouseLeave={() =>
                      setIsPreviewingBroaderElementSelection(false)
                    }
                    onClick={handleUseBroaderElementSelection}
                  >
                    Use larger surrounding panel instead
                  </Button>
                ) : null}
                <Button
                  variant="outlined"
                  color="inherit"
                  onClick={handleRetryElementSelection}
                >
                  Try again
                </Button>
                <Button
                  autoFocus={
                    !pendingSelectionLooksSmall ||
                    !pendingBroaderElementSelection
                  }
                  variant="contained"
                  onClick={handleConfirmElementSelection}
                >
                  Use this area and continue
                </Button>
              </Stack>
            </Stack>
          </Paper>
        </Box>
      ) : null}

      {pickerActive && pickerCursorPosition && !pickerGuidanceDismissed ? (
        <Box
          data-hc-admin-picker-guidance
          sx={{
            position: "fixed",
            left: Math.max(
              8,
              Math.min(
                pickerCursorPosition.x + 14,
                viewportWidth - PICKER_HINT_WIDTH - 8,
              ),
            ),
            top: Math.max(
              8,
              Math.min(
                pickerCursorPosition.y + 14,
                viewportHeight - PICKER_HINT_HEIGHT - 8,
              ),
            ),
            zIndex: (theme) => theme.zIndex.modal + 4,
            pointerEvents: "none",
            maxWidth: PICKER_HINT_WIDTH,
            px: 1.25,
            py: 1,
            borderRadius: 1,
            border: (theme) => `1px solid ${theme.palette.info.main}`,
            backgroundColor: (theme) => alpha(theme.palette.grey[900], 0.92),
            color: "common.white",
            boxShadow: 3,
          }}
        >
          <Typography
            variant="caption"
            sx={{
              display: "block",
              color: "common.white",
              fontWeight: 700,
            }}
          >
            Pick the broad broken area
          </Typography>
          <Typography
            variant="caption"
            sx={{
              display: "block",
              color: "common.white",
              mt: 0.25,
            }}
          >
            Select the full section/card/table, not just a title or button.
          </Typography>
          <Typography
            variant="caption"
            sx={{
              display: "block",
              color: "common.white",
              mt: 0.4,
            }}
          >
            Dismiss: right-click.
          </Typography>
        </Box>
      ) : null}
    </Paper>
  );
};

export default AdminIssueReportWidget;
